import { useState, useCallback, useEffect } from 'react';
import { useUpdateUserRoleMutation } from '../../../api/userApi2';
import { useUserStore, UserRole } from '../../../app/store/useUserStore';
import { trackException } from '../../../shared/utils/telemetry';
import { getRoleIdFromDefaultRoles } from '../../../types/user';
import { SkyporterEvents } from '../../../analytics/skyporterEvents';
import { UserType } from '../../../analytics/dataLayer.types';


interface UseInitialRoleSelectionReturn {
  showDialog: boolean;
  setInitialRole: (role: UserRole) => Promise<void>;
  isSettingRole: boolean;
  error: string | null;
  clearError: () => void;
}

export const useInitialRoleSelection = (): UseInitialRoleSelectionReturn => {
  const [showDialog, setShowDialog] = useState(false);
  const [isSettingRole, setIsSettingRole] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isMounted, setIsMounted] = useState(false);

  const updateUserRoleMutation = useUpdateUserRoleMutation();
  const { user, updateUserRole, hasBasicProfile, defaultRoles } = useUserStore();

  // Handle client-side mounting to avoid hydration mismatch
  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    // Only run after component is mounted on client to avoid hydration mismatch
    if (!isMounted) return;

    // Show dialog immediately for new users after login if they haven't selected a role yet
    // This happens before profile completion check
    if (user && user.auth0_id && !user.user_role) {
      setShowDialog(true);
    } else {
      setShowDialog(false);
    }
  }, [user, isMounted]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const setInitialRole = useCallback(
    async (role: UserRole): Promise<void> => {
      // Validation
      if (!user?.user_id) {
        const errorMsg = 'User not authenticated';
        setError(errorMsg);
        throw new Error(errorMsg);
      }

      const roleId = getRoleIdFromDefaultRoles(role, defaultRoles);

      setIsSettingRole(true);
      setError(null);

      try {

        // Update backend
        await updateUserRoleMutation.mutateAsync({
          user_id: user.user_id,
          role_id: roleId,
        });

        // Update local state
        updateUserRole(role);

        SkyporterEvents.roleSelected({
          userId: user.user_id,
          role: role === UserRole.TRAVELLER ? UserType.Traveller : UserType.Sender,
        });

        // Close dialog
        setShowDialog(false);
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to set role. Please try again.';

        trackException(err as Error, 'useInitialRoleSelection');

        setError(errorMessage);
        throw err;
      } finally {
        setIsSettingRole(false);
      }
    },
    [user, defaultRoles, updateUserRole, updateUserRoleMutation]
  );

  return {
    showDialog,
    setInitialRole,
    isSettingRole,
    error,
    clearError,
  };
};
